import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addPageData, buildWizardPaths, logOut, signIn } from '../actions';
import {
  destroyOrySession,
  getOrySession,
  getSessionToken,
  redirectToSignIn,
} from '../api/OryClient';
import { userSelector } from '../reducers/selectors';

import analytics from './analytics';
import * as payloadFactory from './pageDataPayloadFactory';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [authStates, setAuthStates] = useState({
    isAuthenticated: false,
    isAuthenticating: true,
  });
  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const { isAuthenticated, isAuthenticating } = authStates;

  const handleLogout = async () => {
    const payload = payloadFactory.createAddPageDataPayload('customerGuid', null);
    dispatch(addPageData(payload));
    dispatch(logOut());
    await destroyOrySession();
    setAuthStates({ isAuthenticated: false, isAuthenticating: false });
    redirectToSignIn();
  };

  const pollRememberMeCookie = () => {
    if (isAuthenticated) {
      if (Cookies.get('remember_me') === undefined) {
        // eslint-disable-next-line no-console
        console.log('remember_me cookie not found, logging out');
        // eslint-disable-next-line no-console
        handleLogout();
      } else setTimeout(() => pollRememberMeCookie(), 60000);
    }
  };

  useEffect(() => {
    const initialSession = async () => {
      const session = await getOrySession();
      const {
        session: { id, active, identity },
      } = session;
      const oryUser = {
        session_id: id,
        active,
        access_token: getSessionToken(id, identity.metadata_public.customer_guid),
        token_type: 'Bearer',
        profile: {
          email: identity.traits.email,
          sub: identity.metadata_public.customer_guid,
        },
      };
      const { profile } = oryUser;
      const { sub, email } = profile;
      const customerGuidPayload = payloadFactory.createAddPageDataPayload('customerGuid', sub);
      const userEmailPayload = payloadFactory.createAddPageDataPayload('userEmail', email);
      dispatch(addPageData(customerGuidPayload));
      dispatch(addPageData(userEmailPayload));
      dispatch(buildWizardPaths());
      dispatch(signIn(oryUser));

      const advancedMatching = sub && { external_id: sub };
      analytics.initializePixel(advancedMatching);
    };
    initialSession()
      .then(() => setAuthStates({ isAuthenticated: true, isAuthenticating: false }))
      .catch(() => setAuthStates({ isAuthenticated: false, isAuthenticating: false }));
  }, []);

  useEffect(() => {
    pollRememberMeCookie();
  }, [isAuthenticated]);

  const ctx = {
    isAuthenticated,
    isAuthenticating,
    user,
    handleLogout,
  };

  return <AuthContext.Provider value={{ ...ctx }}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
